/**
 * Top-bar notification bell and popover.
 *
 * Lifted out of `TopBar.tsx` unchanged in behaviour — same 60-second poll,
 * same `notifications-updated` refresh, same click-to-open-and-mark-read, same
 * "View all notifications" footer — and then given the two things the redesign
 * asks of the bell:
 *
 *  - `background`-tier rows never appear here and never inflate the badge;
 *  - a row belonging to a module the user cannot access is not listed, so the
 *    bell and the centre always agree.
 *
 * @link useNotificationUnread for the shared count both bells read.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { notificationsAPI, type Notification } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Bell } from 'lucide-react';
import { cn } from '../../lib/utils';
import { resolveDomain } from './groupNotifications';
import { getSeverityMeta, resolveSeverity } from './severity';
import { useNotificationModuleAccess } from './useNotificationModuleAccess';
import {
  NOTIFICATIONS_UPDATED_EVENT,
  useNotificationUnread,
} from './useNotificationUnread';

const POLL_INTERVAL_MS = 60000;

export default function NotificationBellPopover() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { canSeeDomain, visibleDomains, isLoading: accessLoading } = useNotificationModuleAccess();
  const { unreadCount, refresh } = useNotificationUnread(isAuthenticated);
  // Same scope the badge counts with, so the list can never be shorter than
  // the number above it.
  const domains = visibleDomains.join(',');
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const fetchRecent = useCallback(async () => {
    // Same gate as the unread badge: until permissions/menu settings resolve
    // the domain list is provisional, and fetching on it meant one request
    // with the provisional domains and a second with the real ones.
    if (!isAuthenticated || accessLoading) return;
    try {
      const response = await notificationsAPI.getRecent({
        include_background: false,
        ...(domains ? { domains } : {}),
      });
      setNotifications(Array.isArray(response.data) ? response.data : []);
    } catch (error: unknown) {
      console.error('Error fetching notifications:', error);
    }
  }, [accessLoading, domains, isAuthenticated]);

  useEffect(() => {
    fetchRecent();
    const interval = setInterval(fetchRecent, POLL_INTERVAL_MS);
    const handler = () => {
      fetchRecent();
    };
    window.addEventListener(NOTIFICATIONS_UPDATED_EVENT, handler);
    return () => {
      clearInterval(interval);
      window.removeEventListener(NOTIFICATIONS_UPDATED_EVENT, handler);
    };
  }, [fetchRecent]);

  const visible = notifications.filter(
    (notification) =>
      resolveSeverity(notification) !== 'background' &&
      canSeeDomain(resolveDomain(notification))
  );

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      try {
        await notificationsAPI.markAsRead(notification.id);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n))
        );
        refresh();
      } catch (error: unknown) {
        console.error('Error marking notification as read:', error);
      }
    }
    if (notification.action_url) {
      setOpen(false);
      navigate(notification.action_url);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative hover:bg-gray-100 dark:hover:bg-accent"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5 text-gray-600 dark:text-muted-foreground" aria-hidden="true" />
          {unreadCount > 0 && (
            <span
              className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-white dark:ring-background"
              aria-label={`${unreadCount} new notifications`}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="p-4 border-b border-gray-200 dark:border-border flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-foreground">Notifications</h3>
          {unreadCount > 0 && (
            <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-2 py-0.5 rounded-full">
              {unreadCount} new
            </span>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {visible.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500 dark:text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No notifications</p>
            </div>
          ) : (
            visible.map((notification) => {
              const severityMeta = getSeverityMeta(resolveSeverity(notification));
              return (
                <div
                  key={notification.id}
                  className={cn(
                    'px-4 py-3 hover:bg-gray-50 dark:hover:bg-accent cursor-pointer transition-colors',
                    severityMeta.accentClass,
                    !notification.is_read && 'bg-blue-50 dark:bg-primary/10'
                  )}
                  tabIndex={0}
                  role="button"
                  aria-label={`${notification.title}. ${notification.message}. ${notification.time_ago}`}
                  onClick={() => handleNotificationClick(notification)}
                  onKeyDown={(e) => e.key === 'Enter' && handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p
                        className={cn(
                          'text-sm text-gray-900 dark:text-foreground',
                          !notification.is_read && 'font-medium'
                        )}
                      >
                        {notification.title}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-muted-foreground line-clamp-2">
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs text-gray-500 dark:text-muted-foreground">
                          {notification.time_ago}
                        </p>
                        {severityMeta.severity !== 'info' && (
                          <span
                            className={cn(
                              'text-[10px] px-1.5 py-0.5 rounded-full border',
                              severityMeta.badgeClass
                            )}
                          >
                            {severityMeta.badgeLabel}
                          </span>
                        )}
                      </div>
                    </div>
                    {!notification.is_read && (
                      <span className="h-2 w-2 rounded-full bg-blue-500 mt-2" />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div className="p-3 border-t border-gray-200 dark:border-border">
          <Link
            to="/notifications"
            className="text-sm text-blue-600 hover:text-blue-700 dark:text-primary dark:hover:text-primary/80 font-medium"
            onClick={() => setOpen(false)}
          >
            View all notifications
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
